import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import classNames from 'classnames';
import { Button } from 'lego-on-react';
import * as React from 'react';

import { Bem } from '../../../service/Bem/Bem';
import { divideNumberPositions } from '../../../service/FormatHelpers/FormatHelpers';
import { TestBem } from '../../../service/TestBem';
import { PhrasesSelectorPhrase, PhrasesSelectorWizardId } from '../../../typings/phrases';
import { CheckboxDecorator, CheckboxDecoratorCheckedItem } from '../../CheckboxDecorator/CheckboxDecorator';
import { Loader, LoaderTheme } from '../../Loader/Loader';
import { ScreenErrorMessage } from '../../ScreenErrorMessage/ScreenErrorMessage';
import { Shape2, Shape2Icon } from '../../Shape2/Shape2';
import { Skeleton } from '../../Skeleton/Skeleton';
import { TextInputDecorator } from '../../TextInputDecorator/TextInputDecorator';
import { PhrasesSelectorControlButtons } from '../PhrasesSelectorControlButtons/PhrasesSelectorControlButtons';

import './PhrasesSelectorRefinePhrase.css';
import * as keyset from './PhrasesSelectorRefinePhrase.i18n';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);

export interface PhrasesSelectorRefinePhraseProps {
    isPhrasesLoading: boolean;
    isPhraseShowsLoading: boolean;
    hasMoreRefineWords: boolean;
    phraseShowsPrediction: number;
    refineWords: Array<PhrasesSelectorPhrase>;
    wizardId: PhrasesSelectorWizardId;
    editPhrase: string;
    phrasesError: string;
    phraseShowsError: string;
    keyword: string;
    shows: number;
    phraseWords: Array<string>;
    onPhraseStatsGet: (phrase: string) => void;
    onRefineWordsGet: (phrase: string) => void;
    onPhraseChange: (phrase: string) => void;
    onRefine: (phrase: PhrasesSelectorPhrase) => void;
    onPhraseInfoSet: () => void;
    onCancel: () => void;
}

const BLOCK = 'phrases-selector-refine-phrase';

export class PhrasesSelectorRefinePhrase extends React.PureComponent<PhrasesSelectorRefinePhraseProps> {
    componentDidMount() {
        const { keyword, onRefineWordsGet, onPhraseInfoSet } = this.props;

        onPhraseInfoSet();
        onRefineWordsGet(keyword);
    }

    render() {
        const { isPhraseShowsLoading, editPhrase, onPhraseChange, onCancel } = this.props;

        const saveButtonDisabled = isPhraseShowsLoading || !editPhrase.trim();

        return (
            <div className={Bem.cls(BLOCK)}>
                <div className={Bem.cls(BLOCK, 'header')}>
                    <TextInputDecorator
                        text={editPhrase}
                        theme="normal"
                        view="classic"
                        size="s"
                        cls={classNames(Bem.cls(BLOCK, 'phrase-input'), TestBem.cls(BLOCK, 'phrase-input'))}
                        onChange={onPhraseChange}
                    />
                    <PhrasesSelectorControlButtons
                        disabled={saveButtonDisabled}
                        onReject={onCancel}
                        onAccept={this.onPhraseRefine}
                    />
                </div>
                {this.renderContent()}
            </div>
        );
    }

    private renderContent = () => {
        const { phraseShowsError } = this.props;

        return (
            <>
                <div className={Bem.cls(BLOCK, 'shows-predict')}>
                    <div className={Bem.cls(BLOCK, 'shows-text')}>
                        {i18nRaw('shows-text', {
                            showsCount: this.renderShowsPrediction()
                        })}
                    </div>
                    {phraseShowsError && <div className={Bem.cls(BLOCK, 'shows-error')}>{phraseShowsError}</div>}
                    <span className={Bem.cls(BLOCK, 'refine-note')}>{i18n('refine-note')}</span>
                </div>
                {this.renderRefineWords()}
            </>
        );
    };

    private renderShowsPrediction = () => {
        const { isPhraseShowsLoading, phraseShowsPrediction } = this.props;

        if (isPhraseShowsLoading) {
            return (
                <Skeleton
                    key="predict-shows-skeleton"
                    cls={Bem.cls(BLOCK, 'shows-predict-loader')}
                    isAnimating={true}
                    height={16}
                />
            );
        }

        return (
            <span key="predict-count" className={Bem.cls(BLOCK, 'shows-predict-count')}>
                {divideNumberPositions(phraseShowsPrediction)}
            </span>
        );
    };

    private renderRefineWords = () => {
        const { refineWords, isPhrasesLoading, hasMoreRefineWords } = this.props;

        const hasRefineWords = refineWords.length !== 0;

        return (
            <div className={Bem.cls(BLOCK, 'words-container')}>
                <ul className={Bem.cls(BLOCK, 'words-list')}>{this.renderWordsList()}</ul>
                {hasMoreRefineWords && (
                    <Button
                        cls={Bem.cls(BLOCK, 'more-button')}
                        size="s"
                        theme="clear"
                        tone="default"
                        view="classic"
                        pin="brick-brick"
                        onClick={this.onMoreClick}
                    >
                        <Shape2 icon={Shape2Icon.plus} cls={Bem.cls(BLOCK, 'plus-icon')} width={12} height={12} />
                        {i18n('show-more')}
                    </Button>
                )}
                {isPhrasesLoading && <Loader size="m" theme={!hasRefineWords ? LoaderTheme.white : undefined} />}
            </div>
        );
    };

    private renderEmptyScreen = () => {
        return (
            <ScreenErrorMessage
                cls={Bem.cls(BLOCK, 'status-message')}
                header={i18n('empty-words')}
                text={i18n('empty-words-content')}
            />
        );
    };

    private renderErrorScreen = (errorMessage: string) => {
        return (
            <ScreenErrorMessage
                cls={Bem.cls(BLOCK, 'status-message')}
                header={i18n('error-words')}
                text={errorMessage}
            />
        );
    };

    private renderWordsList = () => {
        const { phraseWords, refineWords, phrasesError } = this.props;

        const hasRefineWords = refineWords.length !== 0;

        if (phrasesError) {
            return this.renderErrorScreen(phrasesError);
        }

        if (!hasRefineWords) {
            return this.renderEmptyScreen();
        }

        return refineWords.map(word => {
            const { name, shows } = word;

            return (
                <li key={name} className={Bem.cls(BLOCK, 'word-item')}>
                    <CheckboxDecorator
                        checked={phraseWords.includes(name)}
                        size="s"
                        theme="normal"
                        tone="default"
                        view="default"
                        item={name}
                        cls={Bem.cls(BLOCK, 'select-word')}
                        onChecked={this.onSelect}
                    />
                    <span className={Bem.cls(BLOCK, 'word-text')}>{name}</span>
                    <span className={Bem.cls(BLOCK, 'word-shows')}>
                        {i18n('word-shows', {
                            count: divideNumberPositions(shows)
                        })}
                    </span>
                </li>
            );
        });
    };

    private onMoreClick = () => {
        const { editPhrase, refineWords, onRefineWordsGet } = this.props;

        onRefineWordsGet(`${editPhrase} ${refineWords.map(w => w.name).join(' ')}`);
    };

    private onSelect = (e: React.KeyboardEvent<HTMLElement>, data: CheckboxDecoratorCheckedItem) => {
        const { editPhrase, phraseWords, onPhraseChange } = this.props;
        const selectedWord = (data.item || '') as string;

        const filteredPhraseWords = phraseWords.filter(word => word !== selectedWord);
        if (phraseWords.length !== filteredPhraseWords.length) {
            onPhraseChange(filteredPhraseWords.join(' '));
        } else {
            onPhraseChange(`${editPhrase} ${selectedWord}`);
        }
    };

    private onPhraseRefine = () => {
        const { editPhrase, phraseShowsPrediction, onRefine } = this.props;

        onRefine({ name: editPhrase, shows: phraseShowsPrediction });
    };
}
